import { useMount } from 'ahooks';
import { Form, Input } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { success as successMessage } from 'components/RevxMessage';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import {
  EmailOtpRequestPayload,
  EmailOtpVerifyPayload,
} from 'domains/appDomain';
import { useUser } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { OAUTH_REDIRECT_SENTINEL } from 'services/appService';
import { canVisitAdminSpace, isSourcer, isSupplier } from 'utils/rbac';

const GENERIC_CONFIRMATION =
  'If the email can access OTP login, a code has been sent.';

const EmailOtpLogin = () => {
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const user = useUser();
  const [requestForm] = Form.useForm<EmailOtpRequestPayload>();
  const [verifyForm] = Form.useForm<EmailOtpVerifyPayload>();
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [resendAvailableAt, setResendAvailableAt] = useState<number>(0);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (step !== 'verify') {
      return;
    }
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [step]);

  const resendSeconds = useMemo(
    () => Math.max(0, Math.ceil((resendAvailableAt - now) / 1000)),
    [now, resendAvailableAt]
  );
  const expirySeconds = useMemo(
    () => Math.max(0, Math.ceil((expiresAt - now) / 1000)),
    [expiresAt, now]
  );

  const redirectAfterLogin = (user: any) => {
    const searchParams = new URLSearchParams(search);
    const redirectUrl = searchParams.get('redirectUrl')?.trim();
    if (redirectUrl) {
      window.location.href = redirectUrl.startsWith('/')
        ? redirectUrl
        : `/${redirectUrl}`;
      return;
    }

    const orderId = searchParams.get('orderId');
    if (orderId) {
      window.location.href = `/order-history?orderId=${encodeURIComponent(
        orderId
      )}`;
      return;
    }

    if (isSupplier(user) || isSourcer(user)) {
      window.location.pathname = '/workspace';
    } else if (canVisitAdminSpace(user)) {
      window.location.pathname = '/platform/org';
    } else {
      window.location.pathname = '/';
    }
  };

  useMount(() => {
    if (user) {
      redirectAfterLogin(user);
      return;
    }

    dispatch.app.logout();
  });

  const syncTimers = (response: {
    resendAvailableInSeconds: number;
    expiresInSeconds: number;
  }) => {
    const current = Date.now();
    setNow(current);
    setResendAvailableAt(current + response.resendAvailableInSeconds * 1000);
    setExpiresAt(current + response.expiresInSeconds * 1000);
  };

  const requestCode = async (values: EmailOtpRequestPayload) => {
    setLoading(true);
    setError(undefined);
    try {
      const normalizedEmail = values.email.trim().toLowerCase();
      const response = await dispatch.app.requestEmailOtp({
        email: normalizedEmail,
      });
      setEmail(normalizedEmail);
      requestForm.setFieldsValue({ email: normalizedEmail });
      verifyForm.setFieldsValue({ email: normalizedEmail, code: '' });
      syncTimers(response);
      setStep('verify');
      successMessage(GENERIC_CONFIRMATION);
    } catch (e: any) {
      setError(
        e?.originalMessage || e?.message || 'Unable to send a code right now.'
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (values: EmailOtpVerifyPayload) => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await dispatch.app.verifyEmailOtp({
        email,
        code: values.code.trim(),
      });
      if (result === OAUTH_REDIRECT_SENTINEL) {
        return;
      }
      if (result.createdUser) {
        successMessage('Your RevShop account was created and signed in.');
      }
      redirectAfterLogin(result.user);
    } catch (e: any) {
      setError(
        e?.originalMessage || e?.message || 'Unable to verify the code.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-form email-otp-form">
      <h1 className="login-form-title">Email Verification Code</h1>
      <p className="email-otp-form-subtitle">
        Use your email to receive a one-time code and sign in without changing
        the standard RevShop login flow.
      </p>
      <RevxSpin spinning={loading}>
        {step === 'request' && (
          <Form form={requestForm} layout="vertical" onFinish={requestCode}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Email is required.' },
                { type: 'email', message: 'Enter a valid email address.' },
              ]}
            >
              <Input
                autoFocus
                placeholder="name@company.com"
                data-testid="input-otp-email"
              />
            </Form.Item>
            <Form.Item className="form-item-button">
              <RevxButton
                size="large"
                htmlType="submit"
                type="primary"
                className="login-form-button"
                data-testid="button-otp-send-code"
              >
                Send code
              </RevxButton>
            </Form.Item>
          </Form>
        )}

        {step === 'verify' && (
          <Form
            form={verifyForm}
            layout="vertical"
            initialValues={{ email, code: '' }}
            onFinish={verifyCode}
          >
            <Form.Item name="email" label="Email">
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="code"
              label="One-time code"
              rules={[
                { required: true, message: 'Enter the code from your email.' },
              ]}
            >
              <Input
                autoFocus
                inputMode="numeric"
                maxLength={6}
                data-testid="input-otp-code"
              />
            </Form.Item>
            <div className="email-otp-form-meta">
              <span>{GENERIC_CONFIRMATION}</span>
              {expirySeconds > 0 && (
                <span>Code expires in {expirySeconds}s.</span>
              )}
            </div>
            <Form.Item className="form-item-button">
              <RevxButton
                size="large"
                htmlType="submit"
                type="primary"
                className="login-form-button"
                data-testid="button-otp-verify"
              >
                Verify and sign in
              </RevxButton>
            </Form.Item>
            <div className="email-otp-form-actions">
              <RevxButton
                type="link"
                disabled={resendSeconds > 0}
                onClick={() => requestCode({ email })}
              >
                {resendSeconds > 0
                  ? `Resend available in ${resendSeconds}s`
                  : 'Resend code'}
              </RevxButton>
              <RevxButton
                type="link"
                onClick={() => {
                  setStep('request');
                  setError(undefined);
                }}
              >
                Use another email
              </RevxButton>
            </div>
          </Form>
        )}

        {error && <div className="email-otp-form-error">{error}</div>}

        <div className="email-otp-form-footer">
          <Link to="/login">Back to standard login</Link>
        </div>
      </RevxSpin>
    </div>
  );
};

export default EmailOtpLogin;
